/* Mímir — 3D brain constellation
 *
 * A rotating shell of particles in a brain-like shape (two hemispheres with a
 * central longitudinal fissure and subtle gyri/sulci displacement). Each node
 * represents a known word: coloured by part of speech, glowing by familiarity
 * strength (0..1) — wire this to spaced-repetition data later.
 *
 * 2D canvas. Painter's-algorithm sort. Drag to rotate. Auto-rotates otherwise.
 *
 * Usage:
 *   const brain = MimirBrain(canvas, {
 *     count: 1500,           // number of word-nodes
 *     pos: { ... },          // override POS palette (see DEFAULT_POS)
 *     autoSpeed: 0.00015,    // rad/ms (gentle yaw)
 *   })
 *   // brain.destroy(); brain.getPalette();
 */

import { cssVar } from '@/utils/cssVar'

export interface StrengthWeight {
  value: number
  weight: number
}

export interface PosPaletteEntry {
  color: string
  share: number
  label: string
  /**
   * Per-POS familiarity distribution. When present, each node in this POS is
   * given a strength by a weighted pick over these values (individual glow).
   * Absent → the engine falls back to a synthetic Gaussian.
   */
  strengths?: StrengthWeight[]
}

export type PosPalette = Record<string, PosPaletteEntry>

export interface MimirBrainOptions {
  count?: number
  pos?: PosPalette
  autoSpeed?: number
  dark?: boolean
  /** 0..1 — how far each node is drawn toward its part of speech's region centre. */
  clusterPull?: number
  /** Screen-space distance, in CSS px, under which two nodes are joined by an edge. */
  linkDistance?: number
}

export const DEFAULT_CLUSTER_PULL = 0
export const DEFAULT_LINK_DISTANCE = 42
// At full pull a node keeps this share of its distance from its region centre —
// clusters separate into distinct lobes without collapsing onto a point.
const MAX_PULL_SHRINK = 0.55

export interface BrainParticle {
  x: number
  y: number
  z: number
  pos: string
  posIdx: number
  strength: number
  depthDim: number
  twinkle: number
  twSp: number
  _px?: number
  _py?: number
  _pz?: number
  _ps?: number
}

export interface MimirBrainInstance {
  destroy: () => void
  getPalette: () => PosPalette
  getParticles: () => BrainParticle[]
}

type Rgb = [number, number, number]

interface Projected {
  x: number
  y: number
  z: number
  s: number
}

interface RegionCenter {
  k: string
  x: number
  y: number
  z: number
}

function rand(a: number, b: number): number { return a + Math.random() * (b - a) }
// Gaussian (normal) random via Box–Muller
function gaussian(mean: number, sd: number): number {
  const u1 = Math.random() || 1e-9, u2 = Math.random()
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
  return mean + z * sd
}
// Weighted pick over a discrete {value, weight} distribution. Returns null when
// the distribution is empty or all weights are non-positive (caller falls back).
function pickStrength(dist: StrengthWeight[]): number | null {
  let total = 0
  for (let i = 0; i < dist.length; i++) total += Math.max(0, dist[i].weight)
  if (total <= 0) return null
  let r = Math.random() * total
  for (let i = 0; i < dist.length; i++) {
    r -= Math.max(0, dist[i].weight)
    if (r <= 0) return dist[i].value
  }
  return dist[dist.length - 1].value
}
function sq(x: number): number { return x * x }
function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v }
function hexRgb(h: string): Rgb {
  h = (h || '#ffffff').replace('#', '')
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
  const n = parseInt(h, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// POS structure (relative share + label). Colour is sourced from CSS at paint
// time — `--art-pos-<key>` in application.css (ADR-005) — so the hex values here
// are only documentation / a fallback for SSR and tests. The CSS variable carries
// the theme-correct value (light/dark) automatically.
export const DEFAULT_POS: PosPalette = {
  noun: { color: '#b29ad8', share: 0.42, label: 'Noun' },
  verb: { color: '#2bb985', share: 0.20, label: 'Verb' },
  adj:  { color: '#e6ba5a', share: 0.10, label: 'Adjective' },
  adv:  { color: '#6f9fd0', share: 0.06, label: 'Adverb' },
  pron: { color: '#5fbcb0', share: 0.05, label: 'Pronoun' },
  prep: { color: '#8f88d0', share: 0.06, label: 'Preposition' },
  det:  { color: '#9a8fb0', share: 0.06, label: 'Determiner' },
  conj: { color: '#4fb89e', share: 0.05, label: 'Conjunction' },
}

export function MimirBrain(canvas: HTMLCanvasElement, opts?: MimirBrainOptions): MimirBrainInstance {
  opts = opts || {}
  const count = opts.count || 1500
  const isDark = opts.dark !== false
  // Colour comes from CSS (--art-pos-<key>), already theme-correct via the .dark
  // class; fall back to the palette's own colour when the var is unset.
  const rawPos = opts.pos || DEFAULT_POS
  const pos: PosPalette = Object.fromEntries(
    Object.entries(rawPos).map(([k, v]) => [k, { ...v, color: cssVar('--art-pos-' + k, v.color) }])
  )
  const posRgb: Record<string, Rgb> = {}
  for (const k in pos) posRgb[k] = hexRgb(pos[k].color)
  const posKeys = Object.keys(pos)
  const posRgbArr = posKeys.map(function (k) { return posRgb[k] })
  const autoSpeed = opts.autoSpeed != null ? opts.autoSpeed : 0.00014
  const pullShrink = 1 - MAX_PULL_SHRINK * clamp(opts.clusterPull ?? DEFAULT_CLUSTER_PULL, 0, 1)
  const linkDistance = opts.linkDistance ?? DEFAULT_LINK_DISTANCE

  // Each POS owns a region of the brain. Particles are assigned to the
  // nearest region centre (Voronoi), so categories cluster into contiguous
  // anatomical zones — verbs left, nouns right, etc.
  const POS_CENTERS: Record<string, Rgb> = {
    noun: [ 0.72,  0.12,  0.15],   // right cerebrum
    verb: [-0.72,  0.12,  0.15],   // left cerebrum
    adj:  [ 0.48,  0.30,  1.05],   // right frontal (front)
    adv:  [-0.48,  0.30,  1.05],   // left frontal
    pron: [ 0.52,  0.05, -1.05],   // right occipital (back)
    prep: [-0.52,  0.05, -1.05],   // left occipital
    det:  [ 0.00, -0.58, -1.05],   // cerebellum (low, back)
    conj: [ 0.00,  0.72, -0.05],   // superior midline (top)
  }
  const centersArr: RegionCenter[] = posKeys.map(function (key): RegionCenter {
    const c = POS_CENTERS[key] || [0, 0, 0]
    return { k: key, x: c[0], y: c[1], z: c[2] }
  })

  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
  let W = 0, H = 0, dpr = 1
  let particles: BrainParticle[] = []
  let rotY = 0.55, rotX = -0.18
  let dragging = false, lastX = 0, lastY = 0
  let velY = 0, velX = 0
  const reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)

  // ---- pre-rendered node sprites (one per POS color) — drawImage is ~10x
  // faster than arc+fill, and sprite already encodes the halo gradient. ----
  const sprites: Record<string, HTMLCanvasElement> = {}
  const SPRITE_SIZE = 64
  function makeSprite(rgb: Rgb): HTMLCanvasElement {
    const s = document.createElement('canvas')
    s.width = SPRITE_SIZE; s.height = SPRITE_SIZE
    const sc = s.getContext('2d') as CanvasRenderingContext2D
    const cx = SPRITE_SIZE / 2
    const grd = sc.createRadialGradient(cx, cx, 0, cx, cx, cx)
    const col = rgb[0] + ',' + rgb[1] + ',' + rgb[2]
    if (isDark) {
      grd.addColorStop(0.00, 'rgba(' + col + ',1)')
      grd.addColorStop(0.10, 'rgba(' + col + ',0.95)')
      grd.addColorStop(0.28, 'rgba(' + col + ',0.45)')
      grd.addColorStop(0.55, 'rgba(' + col + ',0.14)')
      grd.addColorStop(1.00, 'rgba(' + col + ',0)')
    } else {
      // Light mode: larger dense core, slower falloff — glow on cream needs more mass
      grd.addColorStop(0.00, 'rgba(' + col + ',1)')
      grd.addColorStop(0.25, 'rgba(' + col + ',0.90)')
      grd.addColorStop(0.50, 'rgba(' + col + ',0.55)')
      grd.addColorStop(0.80, 'rgba(' + col + ',0.15)')
      grd.addColorStop(1.00, 'rgba(' + col + ',0)')
    }
    sc.fillStyle = grd
    sc.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE)
    return s
  }
  for (const spk in posRgb) sprites[spk] = makeSprite(posRgb[spk])

  // edge colour — warm gold in dark mode, deep ochre in light (cream bg needs contrast)
  const EDGE_RGB: Rgb = isDark ? [216, 168, 102] : [120, 80, 0]
  const EDGE_ALPHAS = isDark
    ? [0.05, 0.10, 0.18, 0.28, 0.42]
    : [0.14, 0.26, 0.42, 0.60, 0.80]
  const NC = posKeys.length // colour index reserved for cross-cluster (neutral) edges

  function nearestPos(x: number, y: number, z: number): number {
    let bi = 0, bd = Infinity
    for (let ci = 0; ci < centersArr.length; ci++) {
      const c = centersArr[ci]
      const dd = sq(x - c.x) + sq(y - c.y) + sq(z - c.z)
      if (dd < bd) { bd = dd; bi = ci }
    }
    return bi
  }

  function resize(): void {
    const r = canvas.getBoundingClientRect()
    W = Math.max(1, r.width); H = Math.max(1, r.height)
    dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = Math.round(W * dpr)
    canvas.height = Math.round(H * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    if (particles.length === 0) build()
  }

  /* Brain implicit volume — union of four shapes (left & right cerebrum
   * hemispheres + cerebellum + brainstem stub). Volume sampled with a soft
   * surface bias: interior nodes are kept but dimmer, surface nodes are
   * denser and brighter, so the silhouette reads clearly while you still get
   * depth inside. */
  function build(): void {
    particles = []
    let tried = 0
    const maxTried = count * 600
    while (particles.length < count && tried < maxTried) {
      tried++
      const x = rand(-1.65, 1.65)
      const y = rand(-1.35, 1.18)
      const z = rand(-1.85, 1.70)

      // longitudinal fissure — clear groove from the top down the midline
      // (only across the cerebrum — not the cerebellum behind/below)
      if (Math.abs(x) < 0.075 && y > 0.20 && z > -0.55) continue

      // cerebrum hemispheres (left/right) — slight forward taper for frontal-lobe feel
      const taper = 1 - 0.07 * Math.max(0, z)
      const rR = sq((x - 0.42) / (0.95 * taper)) + sq(y / 0.92) + sq(z / 1.50)
      const rL = sq((x + 0.42) / (0.95 * taper)) + sq(y / 0.92) + sq(z / 1.50)
      // temporal lobe bulges — lower side flares ahead of the cerebellum
      const rTpR = sq((x - 0.95) / 0.42) + sq((y + 0.30) / 0.40) + sq((z - 0.35) / 0.55)
      const rTpL = sq((x + 0.95) / 0.42) + sq((y + 0.30) / 0.40) + sq((z - 0.35) / 0.55)
      // cerebellum: larger, prominent lobe behind and below
      const rCb = sq(x / 0.72) + sq((y + 0.55) / 0.50) + sq((z + 1.05) / 0.62)
      // brainstem stub
      const rBs = sq(x / 0.16) + sq((y + 1.00) / 0.36) + sq((z + 0.40) / 0.22)
      const r = Math.min(rR, rL, rTpR, rTpL, rCb, rBs)

      // gyri / sulci surface displacement — cerebrum has bold folds, cerebellum has finer ones
      const onCerebellum = rCb < Math.min(rR, rL, rTpR, rTpL, rBs)
      const ripple = onCerebellum
        ? 1.0 + 0.05 * Math.sin(16 * x) * Math.cos(14 * z) + 0.04 * Math.sin(15 * y)
        : 1.0
          + 0.09 * Math.sin(7 * x + 0.5 * y) * Math.cos(6 * z)
          + 0.06 * Math.cos(8 * y + 1.7 + 0.3 * x)
          + 0.04 * Math.sin(9 * z + 0.5 * x)
          // central sulcus hint — a faint cross-fold near the top
          + 0.05 * Math.exp(-sq((z - 0.05) * 4)) * Math.sin(2 * x)

      if (r < ripple + 0.005) {
        // surface closeness: 1 at the boundary, 0 at the centre
        const surfaceCloseness = clamp((r - 0.18) / (ripple - 0.18), 0, 1)
        // acceptance: surface-dense, interior-sparse — keeps the shape readable
        const acceptProb = 0.12 + 0.86 * Math.pow(surfaceCloseness, 1.6)
        if (Math.random() > acceptProb) continue

        const pIdx = nearestPos(x, y, z)
        // Per-node familiarity: weighted pick from this POS's status distribution
        // (individual glow). Falls back to a synthetic bell curve when absent.
        const dist = pos[posKeys[pIdx]]?.strengths
        const picked = dist ? pickStrength(dist) : null
        const s = picked != null ? picked : clamp(gaussian(0.5, 0.18), 0, 1)
        // depth dim: interior nodes glow less so the silhouette stays crisp
        const depthDim = 0.45 + 0.55 * surfaceCloseness

        const c = centersArr[pIdx]
        particles.push({
          x: c.x + (x - c.x) * pullShrink,
          y: c.y + (y - c.y) * pullShrink,
          z: c.z + (z - c.z) * pullShrink,
          pos: posKeys[pIdx], posIdx: pIdx,
          strength: s,
          depthDim: depthDim,
          twinkle: rand(0, Math.PI * 2),
          twSp: rand(0.0006, 0.0016),
        })
      }
    }
  }

  function project(x: number, y: number, z: number): Projected {
    const cy = Math.cos(rotY), sy = Math.sin(rotY)
    const x1 = x * cy + z * sy
    const z1 = -x * sy + z * cy
    const y1 = y
    const cx = Math.cos(rotX), sx = Math.sin(rotX)
    const y2 = y1 * cx - z1 * sx
    const z2 = y1 * sx + z1 * cx
    const D = 4.6
    const pers = D / (D - z2)
    return { x: x1 * pers, y: y2 * pers, z: z2, s: pers }
  }

  let sortedRefs: BrainParticle[] = []
  let lastNow = performance.now()
  function frame(now: number): void {
    const dt = Math.min(50, now - lastNow); lastNow = now
    ctx.clearRect(0, 0, W, H)

    if (!dragging) {
      if (Math.abs(velY) > 0.0001 || Math.abs(velX) > 0.0001) {
        rotY += velY * dt
        rotX += velX * dt
        rotX = clamp(rotX, -0.7, 0.7)
        const decay = Math.pow(0.95, dt / 16.6667)
        velY *= decay; velX *= decay
      } else if (!reduced) {
        rotY += autoSpeed * dt
      }
    }

    const cx = W * 0.5, cy = H * 0.5
    const scale = Math.min(W, H) * 0.36

    if (sortedRefs.length !== particles.length) sortedRefs = particles.slice()
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i]
      const pr = project(p.x, p.y, p.z)
      p._px = cx + pr.x * scale
      p._py = cy - pr.y * scale
      p._pz = pr.z
      p._ps = pr.s
      p.twinkle += p.twSp * dt
    }
    sortedRefs.sort(function (a, b) { return (a._pz as number) - (b._pz as number) })

    // ---- pass 1: edges (proximity, on projected plane) batched by alpha bin ----
    // `cell` is both the grid cell and the link radius: two nodes are joined when they
    // land within it on the projected plane. The neighbour scan below only visits the
    // half-neighbourhood, which is correct exactly while radius == cell size, so these
    // two stay the same number. Widening it is how you get a denser web.
    const cell = linkDistance
    const grid: Record<string, BrainParticle[]> = {}
    for (let j = 0; j < sortedRefs.length; j++) {
      const p2 = sortedRefs[j]
      const gx = ((p2._px as number) / cell) | 0, gy = ((p2._py as number) / cell) | 0
      const k = gx + ',' + gy
      ;(grid[k] || (grid[k] = [])).push(p2)
    }
    // pooled edge batches keyed by (colorIdx * 5 + alphaBin). Intra-cluster
    // edges take the cluster's colour and land in brighter bins; cross-cluster
    // edges are neutral gold and faint — so each region reads as a unit.
    const edgeBuckets: Record<string, number[]> = {}
    for (const key in grid) {
      const parts = key.split(','); const gx2 = +parts[0], gy2 = +parts[1]
      const bucket = grid[key]
      for (let dx = 0; dx <= 1; dx++) {
        for (let dy = (dx === 0 ? 0 : -1); dy <= 1; dy++) {
          const nb = grid[(gx2 + dx) + ',' + (gy2 + dy)]
          if (!nb) continue
          for (let a = 0; a < bucket.length; a++) {
            const pa = bucket[a]
            const bStart = (nb === bucket) ? a + 1 : 0
            for (let b = bStart; b < nb.length; b++) {
              const pb = nb[b]
              const ddx = (pa._px as number) - (pb._px as number), ddy = (pa._py as number) - (pb._py as number)
              const d2 = ddx * ddx + ddy * ddy
              if (d2 >= cell * cell) continue
              const depth = ((pa._pz as number) + (pb._pz as number)) * 0.5
              const depthF = clamp((depth + 1.5) / 3.0, 0, 1)
              const strF = (pa.strength + pb.strength) * 0.5
              const ddim = ((pa.depthDim || 1) + (pb.depthDim || 1)) * 0.5
              const baseA = (1 - Math.sqrt(d2) / cell) * depthF * (0.4 + 0.6 * strF) * ddim
              const samePos = pa.posIdx === pb.posIdx
              const alpha = samePos ? baseA * 0.62 : baseA * 0.14
              // Floor, not a style knob: below this an edge is a grey smear on the
              // background rather than a line. Cross-cluster edges are faint by design
              // (the 0.14 above), so most of them live just over this line.
              if (alpha < 0.008) continue
              const binIdx = Math.min(4, (alpha / 0.10) | 0)
              const colorIdx = samePos ? pa.posIdx : NC
              const bk = colorIdx * 5 + binIdx
              const arr = edgeBuckets[bk] || (edgeBuckets[bk] = [])
              arr.push(pa._px as number, pa._py as number, pb._px as number, pb._py as number)
            }
          }
        }
      }
    }
    ctx.lineWidth = 0.5
    for (const bk in edgeBuckets) {
      const segs = edgeBuckets[bk]; if (!segs.length) continue
      const cIdx = ((+bk) / 5) | 0, binI = (+bk) % 5
      const rgb = cIdx < NC ? posRgbArr[cIdx] : EDGE_RGB
      ctx.strokeStyle = 'rgba(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ',' + EDGE_ALPHAS[binI] + ')'
      ctx.beginPath()
      for (let ei = 0; ei < segs.length; ei += 4) {
        ctx.moveTo(segs[ei], segs[ei + 1])
        ctx.lineTo(segs[ei + 2], segs[ei + 3])
      }
      ctx.stroke()
    }

    // ---- pass 2: nodes via pre-rendered sprite (back-to-front) ----
    for (let i2 = 0; i2 < sortedRefs.length; i2++) {
      const n = sortedRefs[i2]
      const depthF2 = clamp(((n._pz as number) + 1.5) / 3.0, 0, 1)
      const tw = reduced ? 1 : (0.85 + 0.15 * Math.sin(n.twinkle))
      const dd = n.depthDim != null ? n.depthDim : 1
      const baseAlpha = (0.35 + 0.65 * n.strength) * depthF2 * tw * dd
      const alpha = isDark ? baseAlpha : Math.min(1, baseAlpha * 1.8)
      if (alpha < 0.025) continue
      const sizeBase = (0.7 + 1.6 * n.strength) * (n._ps as number)
      const spriteSize = sizeBase * 8.5 // sprite includes the halo, so this covers core + glow
      ctx.globalAlpha = alpha
      ctx.drawImage(sprites[n.pos], (n._px as number) - spriteSize * 0.5, (n._py as number) - spriteSize * 0.5, spriteSize, spriteSize)
    }
    ctx.globalAlpha = 1

    raf = requestAnimationFrame(frame)
  }

  // ---- drag rotation ----
  function onDown(e: PointerEvent): void {
    dragging = true
    lastX = e.clientX; lastY = e.clientY
    if (canvas.setPointerCapture) canvas.setPointerCapture(e.pointerId)
    canvas.style.cursor = 'grabbing'
    velY = 0; velX = 0
    e.preventDefault()
  }
  function onMove(e: PointerEvent): void {
    if (!dragging) return
    const dx = e.clientX - lastX, dy = e.clientY - lastY
    lastX = e.clientX; lastY = e.clientY
    rotY += dx * 0.008
    // drag-down should tilt brain forward (top toward viewer) — invert sign now that screen-y is flipped
    rotX = clamp(rotX - dy * 0.005, -0.7, 0.7)
    velY = (dx * 0.008) / 16.6667
    velX = (-dy * 0.005) / 16.6667
  }
  function onUp(e: PointerEvent): void {
    if (!dragging) return
    dragging = false
    canvas.style.cursor = 'grab'
    try { if (canvas.releasePointerCapture) canvas.releasePointerCapture(e.pointerId) } catch (err) { /* ignore */ }
  }
  canvas.addEventListener('pointerdown', onDown)
  canvas.addEventListener('pointermove', onMove)
  canvas.addEventListener('pointerup', onUp)
  canvas.addEventListener('pointercancel', onUp)
  canvas.style.cursor = 'grab'
  canvas.style.touchAction = 'pan-y'

  let raf: number
  const ro = new ResizeObserver(resize)
  ro.observe(canvas)
  resize()
  raf = requestAnimationFrame(frame)

  return {
    destroy: function (): void {
      cancelAnimationFrame(raf); ro.disconnect()
      canvas.removeEventListener('pointerdown', onDown)
      canvas.removeEventListener('pointermove', onMove)
      canvas.removeEventListener('pointerup', onUp)
      canvas.removeEventListener('pointercancel', onUp)
    },
    getPalette: function (): PosPalette { return pos },
    getParticles: function (): BrainParticle[] { return particles },
  }
}
